// Goon manager — auto-clicker citizens. Each owned goon contributes its
// clicksPerSec to a shared accumulator; when the accumulator crosses 1.0 we
// fire registerClick() and decrement. This naturally piggybacks on the season
// system's per-click logic — yields, progress, and persistence all flow
// through the same path as a human click.

import { Events, emit, on } from '../ui/eventBus.js'
import { GoonConfig, GoonTypes, RaidDamageConfig, CitizenCaps } from '../config/balance.js'
import * as season from './seasonManager.js'
import * as feed from './feedManager.js'
import * as population from './populationManager.js'
import * as events from './eventManager.js'
import * as profile from './profileManager.js'
import * as idle from './idleManager.js'
import * as damage from './damageManager.js'
import { getGoonRateMultiplier } from './buildingState.js'

const accumulators = new Map() // goonId → fractional clicks pending
let lastTick = 0
let rafHandle = null

export function init() {
  on(Events.GoonBuyRequested, ({ goonId }) => buy(goonId))
  lastTick = performance.now()
  loop()
  broadcast()
}

// Re-emit ownership counts for the *active* kingdom. Called on boot and
// after every kingdom switch.
export function broadcast() {
  for (const id of Object.values(GoonTypes)) {
    emit(Events.GoonOwnedChange, { goonId: id, owned: season.getGoonsOwned(id) })
  }
}

export function priceOf(goonId) {
  const cfg = GoonConfig[goonId]
  const owned = season.getGoonsOwned(goonId)
  return Math.ceil(cfg.baseCost * Math.pow(cfg.costGrowth, owned))
}

// CR-3: ownership cap is 25 per type (CitizenCaps), overriding GoonConfig.maxOwned.
function capOf(goonId) {
  return CitizenCaps[goonId] ?? GoonConfig[goonId]?.maxOwned ?? null
}

function buy(goonId) {
  const cap = capOf(goonId)
  const owned = season.getGoonsOwned(goonId)
  if (cap != null && owned >= cap) return false
  const cost = priceOf(goonId)
  if (!season.spendWealth(cost)) return false
  season.incrementGoonsOwned(goonId)
  return true
}

export function isAtCap(goonId) {
  const cap = capOf(goonId)
  if (cap == null) return false
  return season.getGoonsOwned(goonId) >= cap
}

function loop() {
  rafHandle = requestAnimationFrame(loop)
  const now = performance.now()
  const dt = (now - lastTick) / 1000
  lastTick = now

  const profileGoonRate = profile.getBonuses().goonRateBonus ?? 0
  const titleGoonMult = profile.getTitleMultipliers().goonRateMult
  // Real-world boss/worker dynamic: ACTIVE = full engagement, WARNING =
  // workers know the boss looked away → they slack off, FINED = open
  // revolt. idleManager owns the per-state multiplier so all three tiers
  // stay in sync. While a LABOR repair is in progress, citizens are tied
  // up rebuilding → goon income is fully halted (laborGoonRateMult: 0).
  const idleMult = damage.isRepairing()
    ? RaidDamageConfig.laborGoonRateMult
    : idle.getGoonRateMult()
  const rateMult =
    (1 + getGoonRateMultiplier() + feed.getRateMultiplier() + events.getRateBonus() + profileGoonRate) *
    titleGoonMult *
    idleMult
  for (const id of Object.values(GoonTypes)) {
    const owned = season.getGoonsOwned(id) - season.getHospitalizedCount(id) // (patients in the Hospital do not click)
    if (owned <= 0) continue
    const cfg = GoonConfig[id]
    const eduMult = population.goonClickRateMultiplier(id)
    const acc = (accumulators.get(id) ?? 0) + owned * cfg.clicksPerSec * rateMult * eduMult * dt
    const clicks = Math.floor(acc)
    accumulators.set(id, acc - clicks)
    // CR-2: goons no longer fake-click for income (that would secretly fill the
    // harvest cap). Passive citizen income is taxManager's job; this loop now
    // only drives the goon-click VISUAL cadence.
    for (let i = 0; i < clicks; i++) {
      emit(Events.GoonClick, { goonId: id })
    }
  }
}

export function stop() {
  if (rafHandle) cancelAnimationFrame(rafHandle)
  rafHandle = null
}

// PHASE 2.5: per-goon visual (sprite walking the paddy), unique animations.
// PHASE 3: balance pass — diminishing returns, goon synergies, upgrades.
